import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PitBoardLogo from "@/components/PitBoardLogo";
import { Loader2, CheckCircle2, AlertCircle, RefreshCcw } from "lucide-react";

/**
 * Magic Link callback page — l'utilisateur arrive ici depuis le lien de l'email.
 * URL : /auth/magic?token=XXX
 * Échange le token contre une session, puis redirige vers la home.
 */
export default function MagicLinkCallback() {
  const navigate = useNavigate();
  const { refresh: refreshUser } = useAuth();
  const [status, setStatus] = useState("verifying"); // verifying | success | error
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    if (!token) {
      setStatus("error");
      setErrorMsg("Lien invalide — aucun jeton trouvé dans l'URL.");
      return;
    }

    (async () => {
      try {
        const { data } = await api.post("/auth/magic-link-verify", { token });
        if (data?.session_token) {
          try { localStorage.setItem("pitboard_session_token", data.session_token); } catch { /* noop */ }
        }
        await refreshUser?.();
        setStatus("success");
        // Petit délai pour que l'utilisateur voie le check vert
        setTimeout(() => navigate("/", { replace: true }), 800);
      } catch (e) {
        const detail = e?.response?.data?.detail || e?.message || "Échec de la connexion";
        setStatus("error");
        setErrorMsg(typeof detail === "string" ? detail : "Échec de la connexion");
      }
    })();
  }, [navigate, refreshUser]);

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-app text-[var(--chalk)]" data-testid="magic-callback">
      <div className="max-w-md w-full rounded-xl p-8 text-center" style={{ background: "var(--carbon)", border: "1px solid var(--steel)" }}>
        <div className="flex justify-center mb-6">
          <PitBoardLogo size={56} />
        </div>

        {status === "verifying" && (
          <>
            <Loader2 size={36} className="mx-auto mb-4 animate-spin" style={{ color: "var(--pit-yellow)" }} />
            <h2 className="text-xl font-semibold mb-2" style={{ fontFamily: "Georgia, serif" }}>Connexion en cours…</h2>
            <p className="text-[14px]" style={{ color: "var(--ink-soft)" }}>Validation de votre lien magique.</p>
          </>
        )}

        {status === "success" && (
          <>
            <CheckCircle2 size={36} className="mx-auto mb-4" style={{ color: "#22C55E" }} />
            <h2 className="text-xl font-semibold mb-2" style={{ fontFamily: "Georgia, serif" }}>Connecté !</h2>
            <p className="text-[14px]" style={{ color: "var(--ink-soft)" }}>Redirection vers votre tableau de bord…</p>
          </>
        )}

        {status === "error" && (
          <>
            <AlertCircle size={36} className="mx-auto mb-4" style={{ color: "#E10600" }} />
            <h2 className="text-xl font-semibold mb-2" style={{ fontFamily: "Georgia, serif" }}>Connexion impossible</h2>
            <p className="text-[13px] mb-6" style={{ color: "var(--ink-soft)" }} data-testid="magic-error-msg">{errorMsg}</p>
            <button
              onClick={() => { window.location.href = "/login"; }}
              className="btn inline-flex items-center gap-2 px-4 py-2 rounded-lg"
              style={{ background: "transparent", border: "1.5px solid var(--pit-yellow)", color: "var(--pit-yellow)", fontFamily: "'Inter', sans-serif", letterSpacing: 0, textTransform: "none" }}
              data-testid="magic-retry-btn"
            >
              <RefreshCcw size={14} /> Demander un nouveau lien
            </button>
          </>
        )}
      </div>
    </div>
  );
}
